import {
  AsyncAppointment,
  AsyncAppointmentAIInsight,
  AsyncAppointmentApiListAsyncAppointmentsRequest,
  CancelAsyncAppointmentResponse,
  GetAsyncAppointmentResponse,
  GetAsyncAppointmentAIInsightResponse,
  ListAsyncAppointmentsResponse,
  AsyncAppointmentWithAIInsight
} from "@vitable/asclepius-api-client-ts"
import { Ref, reactive, ref, watch } from "vue"

// @ts-ignore
import store from "@/store"
import { useAsclepius } from "@/composables/api/asclepius"

export const useListAsyncAppointments = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()

  const asyncAppointments = reactive<Paginated<AsyncAppointmentWithAIInsight>>({
    currentPage: 1,
    nextPage: undefined,
    prevPage: undefined,
    totalCount: 0,
    results: []
  })

  async function listAsyncAppointments(request: AsyncAppointmentApiListAsyncAppointmentsRequest) {
    await callWithResponse(() => getAsyncAppointmentApi().listAsyncAppointments(request))
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: ListAsyncAppointmentsResponse = callResult.value.getData()
      asyncAppointments.results = data.results
      asyncAppointments.nextPage = data.next
      asyncAppointments.prevPage = data.previous
      asyncAppointments.totalCount = data.count
    }
  })

  return {
    listAsyncAppointments,
    listAsyncAppointmentsResult: callResult,
    asyncAppointments
  }
}

export const useGetAsyncAppointmentById = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()
  const asyncAppointment: Ref<AsyncAppointment | undefined> = ref()

  async function getAsyncAppointmentById(asyncAppointmentId: string): Promise<Ref<AsyncAppointment | undefined>> {
    const response: GetAsyncAppointmentResponse = await callWithResponse(() =>
      getAsyncAppointmentApi().getAsyncAppointment({
        asyncAppointmentId
      })
    )
    asyncAppointment.value = response.async_appointment
    return asyncAppointment
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: GetAsyncAppointmentResponse = callResult.value.getData()
      asyncAppointment.value = data.async_appointment
    }
  })

  return {
    asyncAppointment,
    getAsyncAppointmentById,
    getAsyncAppointmentByIdResult: callResult
  }
}

export const useCancelAsyncAppointment = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()

  const cancelledAsyncAppointment: Ref<AsyncAppointment | undefined> = ref()

  async function cancelAsyncAppointment(asyncAppointmentId: string, cancelReason: string) {
    const asyncAppointmentResponse: CancelAsyncAppointmentResponse = await callWithResponse(() =>
      getAsyncAppointmentApi().cancelAsyncAppointment({
        asyncAppointmentId,
        cancelAsyncAppointmentRequest: { cancel_reason: cancelReason }
      })
    )
    cancelledAsyncAppointment.value = asyncAppointmentResponse.async_appointment
    return cancelledAsyncAppointment
  }

  return {
    cancelledAsyncAppointment,
    cancelAsyncAppointment,
    cancelAsyncAppointmentResult: callResult
  }
}

export const useGetAsyncAppointmentAIInsight = () => {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()
  const aiInsight: Ref<AsyncAppointmentAIInsight | undefined> = ref()

  async function getAsyncAppointmentAIInsight(asyncAppointmentId: string): Promise<void> {
    const response: GetAsyncAppointmentAIInsightResponse = await callWithResponse(() =>
      getAsyncAppointmentApi().getAsyncAppointmentAiInsight({ asyncAppointmentId })
    )
    aiInsight.value = response.ai_insight
  }

  watch(callResult, (newValue) => {
    if (newValue.isSuccess()) {
      const data: GetAsyncAppointmentAIInsightResponse = callResult.value.getData()
      aiInsight.value = data.ai_insight
    }
  })

  return {
    aiInsight,
    getAsyncAppointmentAIInsight,
    getAsyncAppointmentAIInsightResult: callResult
  }
}

export function useUpdateAsyncAppointmentStatus(asyncAppointmentId: string) {
  const { getAsyncAppointmentApi, callWithResponse, callResult } = useAsclepius()

  const isProvider: boolean = store.getters["me/isProvider"]
  const isClinicalAdmin: boolean = store.getters["me/isClinicalAdmin"]

  const asyncAppointment: Ref<AsyncAppointment | undefined> = ref<AsyncAppointment>()

  async function markAsyncAppointmentClaimed(): Promise<void> {
    if (!isProvider) {
      throw new Error("Must be a Provider to claim an Async Appointment!")
    }

    const data = await callWithResponse(() => getAsyncAppointmentApi().claimAsyncAppointment({ asyncAppointmentId }))

    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentUnclaimed(): Promise<void> {
    if (!isProvider) {
      throw new Error("Must be a Provider to unclaim an Async Appointment!")
    }

    const data = await callWithResponse(() => getAsyncAppointmentApi().unclaimAsyncAppointment({ asyncAppointmentId }))
    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentOnHold(): Promise<void> {
    if (!isProvider && !isClinicalAdmin) {
      throw new Error("Must be a Provider or Clinical Admin to place an Async Appointment on hold!")
    }

    const data = await callWithResponse(() =>
      getAsyncAppointmentApi().markAsyncAppointmentOnHold({ asyncAppointmentId })
    )
    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentCharted(): Promise<void> {
    if (!isProvider) {
      throw new Error("Must be a Provider to chart an Async Appointment!")
    }

    const data = await callWithResponse(() =>
      getAsyncAppointmentApi().markAsyncAppointmentCharted({ asyncAppointmentId })
    )

    asyncAppointment.value = data.async_appointment
  }

  async function markAsyncAppointmentClosed(): Promise<void> {
    if (!isProvider && !isClinicalAdmin) {
      throw new Error("Must be a Provider or Clinical Admin to close an Async Appointment!")
    }

    const data = await callWithResponse(() =>
      getAsyncAppointmentApi().markAsyncAppointmentClosed({ asyncAppointmentId })
    )

    asyncAppointment.value = data.async_appointment
  }

  return {
    asyncAppointment,
    markAsyncAppointmentClaimed,
    markAsyncAppointmentUnclaimed,
    markAsyncAppointmentOnHold,
    markAsyncAppointmentCharted,
    markAsyncAppointmentClosed,
    updateAsyncAppointmentStatusResult: callResult
  }
}
